/**
 * Great-circle distance between two WGS84 coordinates.
 *
 * Pure & deterministic. Used for calendar travel-fairness analytics from real
 * stadium coordinates (teams.home_stadium_id → stadiums.location_id →
 * locations.latitude/longitude). Returns kilometres; 0 for identical points.
 *
 * Great-circle (not road) distance — good enough for *relative* fairness
 * comparisons between teams. A future enhancement could swap in the precomputed
 * `location_distances` table for road km.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

const toRad = (deg: number) => (deg * Math.PI) / 180;

export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}
